import { useId, useState } from 'react';

import clsx from 'clsx';
import dayjs from 'dayjs';
import { nanoid } from 'nanoid';
import { Dialog, Tooltip } from 'radix-ui';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';

import Calendar from '@quick/assets/icons/form/calendar.svg';
import Trash from '@quick/assets/icons/trash.svg';
import { Button } from '@quick/components/Button';
import { TimePicker } from '@quick/components/FlatPicker';
import { Modal } from '@quick/components/Modal';
import { Switch } from '@quick/components/Switch';
import type { ICity } from '@quick/go-api-types/services/cities';
import {
  timezoneDateToFieldValue,
  userInputToTimezoneDate,
} from '@quick/utils';

import { useAppTranslation } from '@/app/i18n/client';
import Plus2 from '@/assets/plus2.svg';
import { daysAndMonths } from '@/layers/core/deal/mappers/calendarMapper';
import { useFetch } from '@/layers/core/deal/services/useFetch';

import {
  animationClassName,
  gapClassName,
  groupClassName,
  onlyBorderClassName,
} from './classNames';
import { useFormDataContext } from './FormDataContext';
import { CommonTitle } from './Titles';
import type { IForm } from './types';

export const Dates = () => {
  const { t } = useAppTranslation(['common', 'demand']);
  const {
    watch,
    control,
    formState: { errors },
  } = useFormContext<IForm>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'dates',
  });

  const { data, line } = useFormDataContext();

  const modalityId = watch('modality_id');

  const calendarTranslation = daysAndMonths(t);

  const cityId = watch('stops').find((add) => add.city_id)?.city_id;
  const { data: cities } = useFetch<ICity[]>(
    !!cityId && {
      url: `/services/cities`,
      params: { city_id: cityId },
    }
  );

  const isClean = line?.line_filter === 'clean';

  const timezone = cities?.find((city) => `${city.id}` === `${cityId}`)
    ?.timezone?.name;

  const minDayJs = getMinDayJs(timezone, isClean);
  const minDate = minDayJs.format('YYYY-MM-DD HH:mm');

  const show = !!fields.length;

  if (!modalityId || !data?.lines?.length) return null;
  return (
    <div
      className={clsx(
        'flex flex-col',
        groupClassName,
        gapClassName,
        animationClassName,
        onlyBorderClassName
      )}
    >
      <div className="flex flex-wrap gap-4">
        <CommonTitle title={t('common:dates')} Icon={Calendar} />
        <div className="ml-auto flex flex-wrap justify-end gap-4">
          <Switch
            id="programService"
            checked={!!fields.length}
            onChange={({ target: { checked } }) => {
              if (checked) append({ date_time: '', uuid: nanoid() });
              else remove();
            }}
            className="group items-center justify-end gap-6"
          >
            <span className="group-focus-within:underline">
              {t('demand:schedule')}
            </span>
          </Switch>
          <Button
            className="flex items-center gap-4"
            kind="secondary"
            variant="accept"
            // disabled={!show}
            onClick={() => {
              if (fields.length <= 1000)
                append({ date_time: '', uuid: nanoid() });
            }}
            type="button"
          >
            <Plus2 className="w-4" />
            {t('common:add')}
          </Button>
        </div>
      </div>
      {show && (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t('demand:dates.timezoneHelp')}
        </p>
      )}
      <div className="grid w-full gap-4 empty:hidden sm:grid-cols-[repeat(auto-fit,minmax(14rem,1fr))]">
        {show &&
          fields?.map((date, i) => (
            <div className="flex w-full min-w-0 flex-col" key={date.uuid}>
              <div className="flex w-full min-w-0 items-center">
                <Controller
                  name={`dates.${i}.date_time`}
                  rules={{
                    required: t('common:msg.required_field'),
                    validate: (v) => {
                      if (dayjs(v).isBefore(minDayJs, 'minute'))
                        return t('common:msg.min_equal_number', {
                          min: minDayJs.format('YYYY-MM-DD HH:mm'),
                        });
                    },
                  }}
                  render={({ field }) => (
                    <TimePicker
                      {...field}
                      isOnModal
                      className="w-full"
                      innerClassName="[&>input]:pr-8 [&>input]:min-w-0"
                      ref={(dt) => {
                        field.ref(dt?.flatpickr.input);
                      }}
                      id={`${i}date`}
                      onClose={(_, currentDate) => {
                        const dateWithTimezone = userInputToTimezoneDate(
                          currentDate,
                          timezone
                        );
                        field.onChange(dateWithTimezone);
                      }}
                      onChange={(_, currentDate) => {
                        const dateWithTimezone = userInputToTimezoneDate(
                          currentDate,
                          timezone
                        );
                        field.onChange(dateWithTimezone);
                      }}
                      options={{
                        dateFormat: 'Y-m-d H:i',
                        enableTime: true,
                        minDate,
                        allowInput: true,
                        disableMobile: true,
                        locale: calendarTranslation,
                        allowInvalidPreload: true,
                        parseDate: (date) => {
                          const cleanDate = String(date).replaceAll(
                            // replace all characters that are not part of a date string
                            /[^\dZT\-:./\s]+/g,
                            ''
                          );
                          const parsedDate = dayjs(cleanDate);
                          if (parsedDate.isValid()) return parsedDate.toDate();
                          return new Date();
                        },
                      }}
                      placeholder={t(
                        'demand:dates.form.serviceDatePlaceholder'
                      )}
                      value={
                        field.value
                          ? timezoneDateToFieldValue(field.value, timezone)
                          : ''
                      }
                    />
                  )}
                />
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <button
                      className="hover:bg-gray-light -ml-8 mr-2 aspect-square rounded-full p-1"
                      onClick={() => remove(i)}
                      type="button"
                    >
                      <Trash className="text-red-600" />
                    </button>
                  </Tooltip.Trigger>
                  <Tooltip.Portal>
                    <Tooltip.Content
                      sideOffset={5}
                      side="top"
                      className="data-[state=delayed-open]:data-[side=top]:animate-fade-in-down
                        dark:text-gray-light flex select-none flex-col rounded bg-gray-50 p-1 text-sm
                        shadow duration-200 will-change-transform dark:bg-gray-600"
                    >
                      <p>{t('common:delete')}</p>
                      <Tooltip.Arrow className="fill-gray-50 dark:fill-gray-600" />
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
              </div>
              <p className="text-right text-xs font-semibold text-red-500 empty:hidden dark:text-red-300">
                {errors.dates?.[i]?.date_time?.message || ''}
              </p>
            </div>
          ))}
      </div>
      <div className="flex flex-row flex-wrap items-center justify-center gap-4">
        <CloneDateButton fieldsLength={fields.length} append={append} />
        <AddDateRangeModal
          fieldsLength={fields.length}
          append={append}
          minDate={minDate}
          timezone={timezone}
          remove={remove}
        />
      </div>
    </div>
  );
};

function CloneDateButton({
  fieldsLength,
  append,
}: {
  fieldsLength: number;
  append: (value: IForm['dates'][number]) => void;
}) {
  const { t } = useAppTranslation(['common', 'demand']);
  const { watch, getValues } = useFormContext<IForm>();

  const lastDate = watch(`dates.${fieldsLength - 1}.date_time`);
  if (!lastDate) return null;

  return (
    <Button
      className="flex items-center gap-4 sm:self-center"
      kind="secondary"
      variant="accept"
      disabled={fieldsLength > 1000}
      onClick={() => {
        if (fieldsLength > 1000) return;
        const lastDate = getValues('dates').at(-1)?.date_time;
        if (!lastDate) return;
        append({
          date_time: dayjs(lastDate).add(1, 'day').toISOString(),
          uuid: nanoid(),
        });
      }}
      type="button"
    >
      <Plus2 className="w-4" />
      {t('demand:dates.cloneDate')}
    </Button>
  );
}

function AddDateRangeModal({
  fieldsLength,
  append,
  minDate,
  timezone,
  remove,
}: {
  fieldsLength: number;
  append: (value: IForm['dates'][number]) => void;
  minDate: string;
  timezone: string | undefined;
  remove: (index: number) => void;
}) {
  const { t } = useAppTranslation(['common', 'demand']);
  const { getValues } = useFormContext<IForm>();

  const [isOpen, setIsOpen] = useState(false);

  return (
    <Modal
      className="z-75"
      trigger={
        <Dialog.Trigger asChild>
          <Button
            className="flex items-center gap-4 sm:self-center"
            kind="secondary"
            variant="accept"
            onClick={() => setIsOpen(true)}
            disabled={fieldsLength > 1000}
          >
            <Plus2 className="w-4" />
            {t('demand:dates.addDateRange')}
          </Button>
        </Dialog.Trigger>
      }
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      contentClassName="!bg-bg p-6"
    >
      <AddDateRangeContent
        append={append}
        minDate={minDate}
        timezone={timezone}
        onSave={() => {
          setIsOpen(false);

          const dates = getValues('dates');
          const firstDate = dates.at(0)?.date_time;
          if (firstDate === '') {
            remove(0);
          }
        }}
      />
    </Modal>
  );
}

const separator = ' | ';

function AddDateRangeContent({
  append,
  minDate,
  timezone,
  onSave,
}: {
  append: (value: IForm['dates'][number]) => void;
  minDate: string;
  timezone: string | undefined;
  onSave: () => void;
}) {
  const { t } = useAppTranslation(['common', 'demand']);

  const calendarTranslation = daysAndMonths(t);

  const [dateRange, setDateRange] = useState('');

  const addDates = () => {
    if (dateRange === '') return;
    const [start, end] = dateRange.split(separator);
    if (!end) {
      append({
        date_time: dayjs(start).toISOString(),
        uuid: nanoid(),
      });
      onSave();
      return;
    }
    const dayDiff = dayjs(end)
      .startOf('day')
      .diff(dayjs(start).startOf('day'), 'day');
    for (let i = dayDiff; i >= 0; i--) {
      // use end date hour to take into account user input
      append({
        date_time: dayjs(end).subtract(i, 'day').toISOString(),
        uuid: nanoid(),
      });
    }
    onSave();
  };

  const id = useId();

  return (
    <article className="flex flex-col gap-4">
      <Dialog.Title className="text-center text-lg">
        {t('demand:dates.addDateRange')}
      </Dialog.Title>
      <div className="bg-grp flex flex-col gap-4 rounded p-4 shadow">
        <TimePicker
          isOnModal
          className="w-full"
          innerClassName="[&>input]:pr-8 [&>input]:min-w-0"
          id={`date_range_${id}`}
          onClose={(_, currentDate) => {
            if (!currentDate) return setDateRange('');
            const currentDateRangeArray = currentDate
              .split(separator)
              .map((date) => {
                return userInputToTimezoneDate(date, timezone);
              });
            setDateRange(currentDateRangeArray.join(separator));
          }}
          onChange={(_, currentDate) => {
            if (!currentDate) return setDateRange('');
            const currentDateRangeArray = currentDate
              .split(separator)
              .map((date) => {
                return userInputToTimezoneDate(date, timezone);
              });
            setDateRange(currentDateRangeArray.join(separator));
          }}
          options={{
            dateFormat: 'Y-m-d H:i',
            enableTime: true,
            mode: 'range',
            minDate,
            allowInput: true,
            disableMobile: true,
            locale: { ...calendarTranslation, rangeSeparator: separator },
            allowInvalidPreload: true,
            parseDate: (date) => {
              const cleanDate = String(date).replaceAll(
                // replace all characters that are not part of a date string
                /[^\dZT\-:.|/\s]+/g,
                ''
              );
              const parsedDate = dayjs(cleanDate);
              if (parsedDate.isValid()) return parsedDate.toDate();
              return new Date();
            },
          }}
          placeholder={t('demand:dates.dateRangePlaceholder')}
          value={
            dateRange
              ? dateRange
                  .split(separator)
                  .map((date) => timezoneDateToFieldValue(date, timezone))
                  .join(separator)
              : ''
          }
        />
      </div>
      <Button
        className="flex items-center gap-4 sm:self-center"
        disabled={dateRange === ''}
        onClick={addDates}
        type="button"
      >
        {t('common:add')}
      </Button>
    </article>
  );
}

const getMinDayJs = (timezone: string | undefined, isClean: boolean) => {
  if (!isClean)
    return dayjs()
      .tz(timezone ?? undefined)
      .add(1.25, 'hour');
  const now = dayjs().tz(timezone ?? undefined);
  // if now is after 15:00:00, min date will be after tomorrow, else min date will be tomorrow
  if (now.hour() >= 15) return now.add(2, 'day').startOf('day');
  return now.add(1, 'day').startOf('day');
};
